import React from "react"
import { useQuery } from "@apollo/client"
import gql from "graphql-tag"
import { Link } from "react-router-dom"
import toTitleCase from "../../Functions/toTitleCase"
import "./AutomatedTasksList.css"
import DatetimeRenderer from "../../Material/DatetimeRenderer"

// isArchived/statuses used to be resolver arguments; now they are generic
// filters, composed here rather than surfaced as a user-facing choice since
// this list is a fixed summary, not a filterable view.
const INCIDENT_TASKS_FILTERS = [
    { fieldName: "is_archived", filterType: "equal", filterValues: "false" },
    { fieldName: "status", filterType: "in", filterValues: ["OPEN", "IN_PROGRESS", "INCOMPLETE", "DONE"] },
]

const IncidentTasksQuery = gql`
    query IncidentTasks($incidentId: ID!, $filters: [FilterInput]) {
        allTasks(incidentId: $incidentId, filters: $filters) {
            edges {
                node {
                    id
                    dateAdded
                    title
                    status
                    isAutomated
                    assignedTo {
                        edges {
                            node {
                                id
                                username
                                name
                            }
                        }
                    }
                }
            }
        }
        optionsList: __type(name: "TaskRabbitTaskRabbitStatusChoices") {
            enumValues {
                name
                description
            }
        }
    }
`

const getAssigneeName = (assignee) => assignee?.name || assignee?.username || "--"

const getStatusClass = (status) => {
    if (status === "DONE") return "done"
    if (status === "IN_PROGRESS") return "in-progress"
    if (status === "CANCELED") return "canceled"
    return "open"
}

function AutomatedTasksList({ incidentId }) {
    const boardId = process.env.REACT_APP_INCIDENT_BOARD_ID

    const { data, loading, error } = useQuery(IncidentTasksQuery, {
        variables: { incidentId, filters: INCIDENT_TASKS_FILTERS },
        skip: !incidentId,
        fetchPolicy: "cache-and-network",
    })

    const tasks = data?.allTasks?.edges?.map((edge) => edge?.node) || []
    const statusLabelMap = (data?.optionsList?.enumValues || []).reduce((acc, statusChoice) => {
        acc[statusChoice.name] = statusChoice.description || statusChoice.name
        return acc
    }, {})

    if (loading) {
        return <div className="AutomatedTasksList-empty">Loading tasks...</div>
    }

    if (error) {
        return <div className="AutomatedTasksList-empty">Unable to load tasks.</div>
    }

    if (!tasks.length) {
        return (
            <div className="AutomatedTasksList-empty">
                No tasks found for this incident.
            </div>
        )
    }

    return (
        <div className="AutomatedTasksList">
            <div className="AutomatedTasksList-header">
                <p className="AutomatedTasksList-label mb-0">Date Added</p>
                <p className="AutomatedTasksList-label mb-0">Title</p>
                <p className="AutomatedTasksList-label mb-0">Assigned To</p>
                <p className="AutomatedTasksList-label mb-0">Status</p>
            </div>
            {tasks.map((task) => {
                const assignees = task?.assignedTo?.edges?.map((edge) => edge?.node).filter(Boolean) || []
                const assignedTo = assignees.length > 0 ? assignees.map((assignee) => getAssigneeName(assignee)).join(", ") : "--"
                return (
                    <div key={task?.id} className="AutomatedTasksList-row">
                        <div className="AutomatedTasksList-section">
                            <p className="AutomatedTasksList-value mb-0">{task.dateAdded ? <DatetimeRenderer datetime={task.dateAdded} /> : "--"}</p>
                        </div>

                        <div className="AutomatedTasksList-section">
                            {task?.isAutomated && <i className="fa fa-cogs AutomatedTasksList-automatedIcon" aria-hidden="true" title="Automated task" />}
                            <Link to={`/incident/${incidentId}/#tasks`} className="AutomatedTasksList-titleLink" rel="noopener noreferrer">
                                <span className="AutomatedTasksList-value AutomatedTasksList-title mb-0">{task?.title || "--"}</span>
                            </Link>
                        </div>

                        <div className="AutomatedTasksList-section">
                            <p className="AutomatedTasksList-value mb-0">{assignedTo}</p>
                        </div>

                        <div className="AutomatedTasksList-section">
                            <span className={`AutomatedTasksList-statusBadge ${getStatusClass(task?.status)}`}>
                                {task?.status ? (statusLabelMap[task.status] || toTitleCase(task.status)) : "--"}
                            </span>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default AutomatedTasksList
